import React, { useEffect } from 'react';
import {
  DailyAudioTrack,
  DailyVideo,
  useDaily,
  useParticipantIds,
} from '@daily-co/daily-react';

export interface CallInnerProps {
  url: string;
  showLocal: boolean;
  onError: (msg: string) => void;
  onEnd: () => void;
  needsRecording: boolean;
}

export const CallInner: React.FC<CallInnerProps> = ({
  url,
  showLocal,
  onError,
  onEnd,
  needsRecording,
}) => {
  const daily = useDaily();
  const remoteIds = useParticipantIds({ filter: 'remote' });
  const localIds = useParticipantIds({ filter: 'local' });

  useEffect(() => {
    if (!daily) return;
    const onLeft = () => onEnd();
    const onErrorEv = (ev: any) => onError(ev?.errorMsg ?? 'Call error');
    daily.on('left-meeting', onLeft);
    daily.on('error', onErrorEv);
    daily
      .join({ url, userName: 'user', videoSource: true, audioSource: true })
      .then(() => {
        // Ensure mic is unmuted after successful join
        try {
          daily.setLocalAudio(true);
          if (needsRecording) {
            daily.startRecording();
          }
        } catch {}
      })
      .catch((e: unknown) => {
        const msg =
          typeof e === 'object' && e && 'message' in e
            ? String((e as any).message)
            : 'Failed to join the call';
        onError(msg);
      });
    return () => {
      if (needsRecording) {
        daily.stopRecording();
      }
      daily.off('left-meeting', onLeft);
      daily.off('error', onErrorEv);
    };
  }, [daily, url, onEnd, onError, needsRecording]);

  const remoteId = remoteIds[0];
  const localId = localIds[0];

  return (
    <div className='relative h-full w-full rounded-lg bg-black shadow-sm ring-1 ring-border'>
      {remoteId ? (
        <DailyVideo
          type='video'
          sessionId={remoteId}
          className='absolute inset-0 h-full w-full rounded-lg object-cover'
        />
      ) : (
        <div className='absolute inset-0 grid place-items-center text-muted-foreground'>
          Waiting for journalist to join...
        </div>
      )}
      {/* Remote audio playback elements (hidden) */}
      {remoteIds.map(rid => (
        <DailyAudioTrack key={rid} sessionId={rid} className='hidden' />
      ))}
      {/* Local preview intentionally removed */}
      {showLocal && localId && null}
    </div>
  );
};
